import { type JSX, type ReactNode, type UIEventHandler, useEffect, useRef, useState } from 'react';
import { cx } from '@linaria/core';

import { LeftScrollButtonWithFade, RightScrollButtonWithFade } from './FadeScrollButtons';
import { buttonHide, buttonSlideOut, LeftButton, OuterWrapper, RightButton, scrollingClass } from './style';

export interface ScrollableProps {
  buttonsWithFade?: boolean;
  children: ReactNode | ReactNode[];
  id?: string;
  innerClass?: string;
  isScrollableCallback?: () => boolean;
  leftBtnDataTrack?: string;
  leftButtonClass?: string;
  outerClass?: string;
  rightBtnDataTrack?: string;
  rightButtonClass?: string;
  scrollIncrement?: number;
  scrollableRef?: React.RefObject<HTMLDivElement | null> | null;
}

export const Scrollable = ({
  buttonsWithFade,
  children,
  id = 'scrollable-id',
  innerClass = '',
  isScrollableCallback = () => true,
  leftBtnDataTrack = '',
  leftButtonClass = '',
  outerClass = '',
  rightBtnDataTrack = '',
  rightButtonClass = '',
  scrollIncrement,
  scrollableRef = null,
}: ScrollableProps): JSX.Element => {
  const scrollElementRef = useRef<HTMLDivElement>(null);

  // start with buttons hidden for ssr
  const [isScrollable, setIsScrollable] = useState(false);
  const [leftButtonHidden, setLeftButtonHidden] = useState(true);
  const [rightButtonHidden, setRightButtonHidden] = useState(true);

  const handleScrollButtonPlacement: UIEventHandler<HTMLDivElement> = event => {
    const scrollElement = event.target as HTMLDivElement;
    const maxScrollLeft = scrollElement.scrollWidth - scrollElement.clientWidth - 1;
    const scrollLeft = scrollElement.scrollLeft;

    if (scrollLeft === 0) {
      setLeftButtonHidden(true);
      setRightButtonHidden(false);
    } else if (scrollLeft >= maxScrollLeft) {
      setLeftButtonHidden(false);
      setRightButtonHidden(true);
    } else {
      setLeftButtonHidden(false);
      setRightButtonHidden(false);
    }
  };

  const handleGridScroll = (scrollElement: HTMLDivElement, scrollLeft: number) => {
    scrollElement.scrollLeft = scrollLeft;
  };

  const handleScrollLeftClick = () => {
    const scrollElement = scrollElementRef.current;
    if (scrollElement) {
      handleGridScroll(scrollElement, scrollIncrement ? scrollElement.scrollLeft - scrollIncrement : 0);
    }
  };

  const handleScrollRightClick = () => {
    const scrollElement = scrollElementRef.current;
    if (scrollElement) {
      const maxScrollLeft = scrollElement.scrollWidth - scrollElement.clientWidth;
      if (scrollIncrement) {
        scrollElement.scrollLeft + scrollIncrement < maxScrollLeft
          ? handleGridScroll(scrollElement, scrollElement.scrollLeft + scrollIncrement)
          : handleGridScroll(scrollElement, maxScrollLeft);
      } else {
        handleGridScroll(scrollElement, maxScrollLeft);
      }
    }
  };

  useEffect(() => {
    setIsScrollable(() => isScrollableCallback());
  }, [isScrollableCallback]);

  useEffect(() => {
    const scrollElement = scrollElementRef.current;

    if (isScrollable) {
      if (scrollElement) {
        handleGridScroll(scrollElement, 0);
        setRightButtonHidden(scrollElement.scrollWidth <= scrollElement.clientWidth);
        setLeftButtonHidden(true);
      }
    } else {
      setRightButtonHidden(true);
      setLeftButtonHidden(true);
    }
  }, [isScrollable]);

  return (
    <OuterWrapper className={outerClass} ref={scrollableRef}>
      <div
        className={cx(isScrollable && scrollingClass, innerClass)}
        id={id}
        onScroll={handleScrollButtonPlacement}
        ref={scrollElementRef}
      >
        {children}
      </div>
      {buttonsWithFade ? (
        <>
          <LeftScrollButtonWithFade
            data-testid="scrollable-left-button"
            dataTrack={leftBtnDataTrack}
            handleScrollLeftClick={handleScrollLeftClick}
            isScrollable={isScrollable}
            leftButtonClass={leftButtonClass}
            leftButtonHidden={leftButtonHidden}
          />
          <RightScrollButtonWithFade
            data-testid="scrollable-right-button"
            dataTrack={rightBtnDataTrack}
            handleScrollRightClick={handleScrollRightClick}
            isScrollable={isScrollable}
            rightButtonClass={rightButtonClass}
            rightButtonHidden={rightButtonHidden}
          />
        </>
      ) : (
        <>
          <LeftButton
            className={cx(leftButtonClass, leftButtonHidden && buttonSlideOut, !isScrollable && buttonHide)}
            data-testid="scrollable-left-button"
            onClick={handleScrollLeftClick}
            {...(leftBtnDataTrack ? { 'data-track': leftBtnDataTrack } : {})}
          />
          <RightButton
            className={cx(rightButtonClass, rightButtonHidden && buttonSlideOut, !isScrollable && buttonHide)}
            data-testid="scrollable-right-button"
            onClick={handleScrollRightClick}
            {...(rightBtnDataTrack ? { 'data-track': rightBtnDataTrack } : {})}
          />
        </>
      )}
    </OuterWrapper>
  );
};
